// src/components/consent/CookieConsentBanner.jsx
/**
 * CookieConsentBanner
 * -------------------
 * Minimal consent UI for storage buckets:
 *  • Essential storage (required for persistent sign-in / "remember me")
 *  • Analytics (future ticket; no-op for now beyond storing preference)
 *
 * Behaviour
 * ---------
 * • Shown only if consent.status === "unknown".
 * • "Accept all" -> essential=true, analytics=true
 * • "Reject non-essential" -> essential=true, analytics=false
 * • "Manage" expands granular toggles:
 *     - Essential is always enabled and cannot be disabled (required for consent storage
 *       and for enabling persistent sign-in when the user opts in).
 *
 * Security notes
 * --------------
 * • No HTML injection; plain strings only.
 * • Consent state stored by ConsentContext; tokens remain managed by Firebase SDK.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useConsent, useConsentActions } from "../../context/ConsentContext";

export default function CookieConsentBanner() {
  const { t } = useTranslation();
  const consent = useConsent();
  const { setChoices } = useConsentActions();

  const [showManage, setShowManage] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  const isVisible = consent?.status !== "set";

  // Keep initial toggle aligned with stored defaults (if any)
  useEffect(() => {
    setAnalytics(!!consent?.analytics);
  }, [consent?.analytics]);

  const acceptAll = useCallback(() => {
    setChoices({ essential: true, analytics: true });
  }, [setChoices]);

  const rejectNonEssential = useCallback(() => {
    // Essential remains allowed so we can store consent and allow policy-driven sign-in persistence.
    setChoices({ essential: true, analytics: false });
  }, [setChoices]);

  const saveManage = useCallback(() => {
    setChoices({ essential: true, analytics });
  }, [setChoices, analytics]);

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] border-t bg-white p-4 md:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={t("consent.title", "Cookie preferences")}
    >
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-4">
        <div className="flex flex-col gap-2">
          <p className="text-base font-semibold text-primary">
            {t("consent.title", "Cookie preferences")}
          </p>
          <p className="text-sm text-greyscale-2">
            {t(
              "consent.body",
              "We use essential storage to keep you signed in (if you choose) and to remember your preferences. Analytics storage is optional and will be used in future to help improve the app.",
            )}
          </p>
        </div>

        {showManage && (
          <div className="rounded-lg border p-3 md:p-4">
            <div className="flex flex-col gap-3">
              {/* Essential (locked on) */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col">
                  <p className="text-sm font-semibold">
                    {t("consent.essentialTitle", "Essential storage")}
                  </p>
                  <p className="text-sm text-greyscale-2">
                    {t(
                      "consent.essentialBody",
                      "Required to remember your cookie choices. If enabled, it also allows a persistent sign-in across browser restarts.",
                    )}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={true}
                    readOnly
                    aria-label={t("consent.essentialAlwaysOn", "Always on")}
                  />
                  <span className="text-sm text-greyscale-2">
                    {t("consent.alwaysOn", "Always on")}
                  </span>
                </div>
              </div>

              {/* Analytics (optional) */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col">
                  <p className="text-sm font-semibold">
                    {t("consent.analyticsTitle", "Analytics")}
                  </p>
                  <p className="text-sm text-greyscale-2">
                    {t(
                      "consent.analyticsBody",
                      "Optional. Enables analytics tags/cookies (not required for sign-in).",
                    )}
                  </p>
                </div>

                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  aria-label={t("consent.analyticsToggle", "Toggle analytics")}
                />
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-2 md:flex-row">
            <button
              type="button"
              className="primary-button w-full md:w-auto"
              onClick={acceptAll}
            >
              {t("consent.acceptAll", "Accept all")}
            </button>

            <button
              type="button"
              className="small-outlined-button w-full md:w-auto"
              onClick={rejectNonEssential}
            >
              {t("consent.rejectNonEssential", "Reject non-essential")}
            </button>

            <button
              type="button"
              className="link w-full text-left md:w-auto md:text-center"
              onClick={() => setShowManage((v) => !v)}
            >
              {showManage
                ? t("consent.hide", "Hide")
                : t("consent.manage", "Manage")}
            </button>
          </div>

          {showManage && (
            <button
              type="button"
              className="primary-button w-full md:w-auto"
              onClick={saveManage}
            >
              {t("consent.save", "Save preferences")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
